import { useCallback } from 'react';
import { useAskStanleyStore } from '@/stores/askStanleyStore';
import { useUser } from '@/queries/useUser';
import { stanAnalytics } from '@/lib';
import { handleCopyMessage, handleGiveFeedback, handleSendMessage } from '../logic';
import { ChatMessage } from '../types/askStanley.types';
import { ASK_STANLEY_CONSTANTS } from '../constants/askStanley.constants';

export function useAskStanley() {
    // Get state
    const isOpen = useAskStanleyStore(state => state.isOpen);
    const currentChatId = useAskStanleyStore(state => state.currentChatId);
    const chats = useAskStanleyStore(state => state.chats);
    const status = useAskStanleyStore(state => state.status);
    const error = useAskStanleyStore(state => state.error);
    const generatedPageData = useAskStanleyStore(state => state.generatedPageData);
    const setGeneratedPageData = useAskStanleyStore(state => state.setGeneratedPageData);

    // Computed values
    const isLoading = useAskStanleyStore(state => {
        const currentChat = state.chats.find(chat => chat.id === state.currentChatId);
        return currentChat?.loading || false;
    });
    const isStreaming = useAskStanleyStore(state => {
        const currentChat = state.chats.find(chat => chat.id === state.currentChatId);
        return currentChat?.isStreaming || false;
    });

    // Get pure state management methods
    const addMessage = useAskStanleyStore(state => state.addMessage);
    const updateMessage = useAskStanleyStore(state => state.updateMessage);
    const updateMessageId = useAskStanleyStore(state => state.updateMessageId);
    const updateChatId = useAskStanleyStore(state => state.updateChatId);
    const updateChatTitle = useAskStanleyStore(state => state.updateChatTitle);
    const setLoading = useAskStanleyStore(state => state.setLoading);
    const setStreaming = useAskStanleyStore(state => state.setStreaming);
    const setAbortController = useAskStanleyStore(state => state.setAbortController);
    const updateMessageFeedback = useAskStanleyStore(state => state.updateMessageFeedback);
    const removeMessage = useAskStanleyStore(state => state.removeMessage);
    const setError = useAskStanleyStore(state => state.setError);
    const setStatus = useAskStanleyStore(state => state.setStatus);
    const generateThreadName = useAskStanleyStore(state => state.generateThreadName);

    // Get basic actions
    const openChat = useAskStanleyStore(state => state.openChat);
    const closeChat = useAskStanleyStore(state => state.closeChat);
    const createNewChat = useAskStanleyStore(state => state.createNewChat);
    const switchToChat = useAskStanleyStore(state => state.switchToChat);
    const abortCurrentRequest = useAskStanleyStore(state => state.abortCurrentRequest);

    const { user } = useUser();

    // Implement sendMessage using logic
    const sendMessage = useCallback(
        async (content: string, attachments: File[] = []) => {
            if (!content.trim() && attachments.length === 0) return;

            // Get the current chat ID from the store at call time (not from closure)
            // This ensures we always use the latest chat ID, even if the hook hasn't re-rendered yet
            const latestChatId = useAskStanleyStore.getState().currentChatId;

            // Get chats from store to check if this is the first message (before adding messages)
            const latestChats = useAskStanleyStore.getState().chats;
            const currentChat = latestChats.find(chat => chat.id === latestChatId);
            const isFirstMessage = currentChat && currentChat.messages.length === 0;

            // Track message sent event
            stanAnalytics('chat-v2-message-sent', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: {
                    chat_id: latestChatId,
                    message_length: content.length,
                    attachments_count: attachments.length,
                    platform: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
                },
            });

            // Clear the status text immediately when sending a message
            setStatus('');

            // Add user message
            const userMessage: ChatMessage = {
                id: Date.now(),
                content,
                timestamp: new Date(),
                type: ASK_STANLEY_CONSTANTS.CHAT_MESSAGE_TYPES.SENT,
                attachments,
            };

            addMessage(latestChatId, userMessage);

            // Create AI placeholder message immediately for thinking indicator
            const aiPlaceholderId = `temp-${Date.now()}`;
            const aiPlaceholder: ChatMessage = {
                id: aiPlaceholderId,
                content: '',
                timestamp: new Date(),
                type: ASK_STANLEY_CONSTANTS.CHAT_MESSAGE_TYPES.RECEIVED,
            };
            addMessage(latestChatId, aiPlaceholder);

            // Generate thread name if this is the first message
            if (
                isFirstMessage &&
                currentChat &&
                (!currentChat.title ||
                    currentChat.title === ASK_STANLEY_CONSTANTS.NEW_CHAT_TITLE ||
                    currentChat.title === ASK_STANLEY_CONSTANTS.DEFAULT_CHAT_TITLE)
            ) {
                updateChatTitle(latestChatId, generateThreadName(content));
            }

            // Set loading state
            setLoading(latestChatId, true);
            setStreaming(latestChatId, true);
            setError(null);

            // Create abort controller
            const abortController = new AbortController();
            setAbortController(latestChatId, abortController);

            try {
                await handleSendMessage(
                    {
                        content,
                        attachments,
                        currentChatId: latestChatId,
                        user,
                        signal: abortController.signal,
                        placeholderMessageId: aiPlaceholderId,
                    },
                    {
                        onChatIdUpdate: (oldId, newId) => {
                            updateChatId(oldId, newId);
                        },
                        onMessageUpdate: (chatId, messageId, updates) => {
                            updateMessage(chatId, messageId, updates);
                        },
                        onMessageIdUpdate: (chatId, oldMessageId, newMessageId) => {
                            updateMessageId(chatId, oldMessageId, newMessageId);
                        },
                        onStatusMessage: (chatId, statusId, content) => {
                            const statusMessage: ChatMessage = {
                                id: statusId,
                                content,
                                timestamp: new Date(),
                                type: 'status',
                            };
                            // Check if status message already exists
                            const chat = chats.find(c => c.id === chatId);
                            const exists = chat?.messages.some(msg => msg.id === statusId);
                            if (!exists) {
                                addMessage(chatId, statusMessage);
                            } else {
                                updateMessage(chatId, statusId, { content });
                            }
                            setStatus(content);
                        },
                        onRemoveStatusMessage: (chatId, statusId) => {
                            removeMessage(chatId, statusId);
                        },
                        onError: errorMsg => {
                            setError(errorMsg);
                        },
                        onComplete: () => {
                            // Get the current chat ID from store (might have been updated)
                            const finalChatId = useAskStanleyStore.getState().currentChatId;
                            setLoading(finalChatId, false);
                            setStreaming(finalChatId, false);
                            setAbortController(finalChatId, null);
                        },
                    }
                );
            } catch (error) {
                console.error(error);
            }
        },
        [
            // Note: currentChatId is no longer in dependencies since we read it from store at call time
            user,
            chats,
            addMessage,
            updateMessage,
            updateMessageId,
            updateChatId,
            updateChatTitle,
            setLoading,
            setStreaming,
            setAbortController,
            removeMessage,
            setError,
            setStatus,
            generateThreadName,
        ]
    );

    // Implement refreshMessage with analytics
    const refreshMessage = useCallback(
        (messageId: string | number, originalQuestion: string, attachments: File[] = []) => {
            // Track refresh event
            stanAnalytics('chat-v2-refresh', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: {
                    chat_id: currentChatId,
                    message_id: messageId,
                    platform: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
                },
            });
            sendMessage(originalQuestion, attachments);
        },
        [sendMessage, currentChatId, user]
    );

    // Implement giveFeedback using logic
    const giveFeedback = useCallback(
        async (messageId: string | number, feedback: 1 | -1) => {
            await handleGiveFeedback(
                {
                    messageId,
                    feedback,
                    currentChatId,
                    chats,
                    user,
                },
                {
                    onFeedbackUpdate: (msgId, feedbackValue) => {
                        updateMessageFeedback(currentChatId, msgId, feedbackValue);
                    },
                    onError: errorMsg => {
                        setError(errorMsg);
                    },
                }
            );
        },
        [currentChatId, chats, user, updateMessageFeedback, setError]
    );

    // Implement copyMessage using logic
    const copyMessage = useCallback(
        async (messageId: string | number) => {
            await handleCopyMessage({
                messageId,
                currentChatId,
                chats,
                user,
            });
        },
        [currentChatId, chats, user]
    );

    // Wrap openChat with analytics
    const openChatWithAnalytics = useCallback(
        (location?: string) => {
            stanAnalytics('ask-stanley-chat-opened', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: {
                    location: location || 'unknown',
                    is_opening: true,
                },
            });
            openChat();
        },
        [openChat, user]
    );

    // Wrap createNewChat with analytics
    const createNewChatWithAnalytics = useCallback(() => {
        // Don't create a new chat if the current chat is empty
        const currentChat = chats.find(chat => chat.id === currentChatId);
        // Only count actual messages (sent/received), not temporary status messages
        const hasMessages =
            currentChat && currentChat.messages.some(msg => msg.type === 'sent' || msg.type === 'received');
        if (!hasMessages) {
            // Current chat is empty, don't create a new one
            return;
        }

        stanAnalytics('ask-stanley-new-chat-clicked', {
            meta: { user_id: user?.user_id, username: user?.username },
            props: {
                current_chat_id: currentChatId,
                total_chats: chats.length,
            },
        });
        createNewChat();
    }, [createNewChat, currentChatId, chats, user]);

    // Wrap switchToChat with analytics
    const switchToChatWithAnalytics = useCallback(
        (chatId: string | number) => {
            const targetChat = chats.find(chat => chat.id === chatId);
            stanAnalytics('ask-stanley-chat-switched', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: {
                    from_chat_id: currentChatId,
                    to_chat_id: chatId,
                    to_chat_title: targetChat?.title || 'Unknown',
                },
            });
            switchToChat(chatId);
        },
        [switchToChat, currentChatId, chats, user]
    );

    // Wrap closeChat with analytics
    const closeChatWithAnalytics = useCallback(() => {
        stanAnalytics('ask-stanley-chat-closed', {
            meta: { user_id: user?.user_id, username: user?.username },
            props: {
                chat_id: currentChatId,
                total_chats: chats.length,
            },
        });
        closeChat();
    }, [closeChat, currentChatId, chats, user]);

    return {
        // State
        isOpen,
        currentChatId,
        chats,
        status,
        error,
        generatedPageData,
        isLoading,
        isStreaming,

        // Actions
        openChat: openChatWithAnalytics,
        closeChat: closeChatWithAnalytics,
        sendMessage,
        createNewChat: createNewChatWithAnalytics,
        switchToChat: switchToChatWithAnalytics,
        refreshMessage,
        giveFeedback,
        copyMessage,
        abortCurrentRequest,
        setGeneratedPageData,
    };
}
